import { assertNever } from '../../exhaustive';

/**
 * The DIN value scale, on its own.
 *
 * This lives apart from `protocol.ts` because the rail catalog needs the value
 * a channel rests at, while `protocol.ts` needs the rail types to spell out
 * which module drives which family. Keeping the scale in a leaf module is what
 * lets both read it without the two importing each other.
 */

/**
 * `value` in `dinoperate`, per the module's own scale:
 *
 * - 0 is off, and is also how a motor is told to stop.
 * - Levels run 10–100; the firmware clamps anything 1–9 up to 10 and anything
 *   above 100 down to 100. The guest path clamps before sending and shows the
 *   clamped value; the console only offers values that need no clamp.
 * - 101 and 102 are the direction codes: decrease and increase.
 *
 * Curtain modules additionally accept 0/1/2, but the reference does not say which
 * is which, so motors are driven with the unambiguous direction codes.
 */
export const DIN_VALUE = {
  MIN: 0,
  /** Lowest level a module acts on. */
  MIN_LEVEL: 10,
  MAX: 100,
  DECREASE: 101,
  INCREASE: 102,
} as const;

/** Label of a DIN value: a level, or one of the two direction codes. */
export const dinValueLabel = (value: number): string => {
  if (value === DIN_VALUE.DECREASE) return 'Decrease';
  if (value === DIN_VALUE.INCREASE) return 'Increase';
  if (value === DIN_VALUE.MIN) return 'Off';
  return `${value}%`;
};

/** Levels the console offers per channel. 0 is off, 100 is full. */
export const DIN_LEVEL_PRESETS = [0, 10, 25, 50, 75, 100] as const;

/**
 * 0–100 on a module's own scale. Zero means off and stays zero; 1–9 is lifted to
 * the floor the firmware would apply anyway, so a payload preview and a stored
 * mapping both show what the device will act on.
 */
export const toDimmerValue = (percent: number): number => {
  const clamped = Math.min(Math.max(percent, DIN_VALUE.MIN), DIN_VALUE.MAX);
  return clamped <= DIN_VALUE.MIN ? DIN_VALUE.MIN : Math.max(clamped, DIN_VALUE.MIN_LEVEL);
};

export const DIN_CHANNEL_MAX = 15;

/**
 * A module's address is a separate thing from a channel, and it does not share
 * the channel's numbering: the reference has an address at 0–15 while a channel
 * counts from one. A rail's address goes on the wire untranslated.
 */
export const DIN_ADDRESS_MIN = 0;
export const DIN_ADDRESS_MAX = 15;

/** What a relay module is told to do. 0 and 1 are the switch itself. */
export const RELAY_VALUE = {
  OFF: 0,
  ON: 1,
  TOGGLE: 2,
  BELL: 4,
} as const;

export type RelayValue = (typeof RELAY_VALUE)[keyof typeof RELAY_VALUE];

/** What each relay value does, for buttons and for the event feed. */
export const RELAY_VALUE_LABEL = {
  [RELAY_VALUE.OFF]: 'Off',
  [RELAY_VALUE.ON]: 'On',
  [RELAY_VALUE.TOGGLE]: 'Toggle',
  [RELAY_VALUE.BELL]: 'Bell',
} as const satisfies Record<RelayValue, string>;

/** Label of a relay value the console did not build itself, e.g. from a report. */
export const relayValueLabel = (value: number): string =>
  Object.entries(RELAY_VALUE_LABEL).find(([key]) => Number(key) === value)?.[1] ?? String(value);

/** Motor directions, as the guest sees them. */
export const TRAVEL = {
  OPEN: 'open',
  STOP: 'stop',
  CLOSE: 'close',
} as const;

export type TravelKind = (typeof TRAVEL)[keyof typeof TRAVEL];

/** The words a motor's directions go by, for buttons and for the feed. */
export const TRAVEL_LABEL = {
  [TRAVEL.OPEN]: 'Open',
  [TRAVEL.STOP]: 'Stop',
  [TRAVEL.CLOSE]: 'Close',
} as const satisfies Record<TravelKind, string>;

/**
 * A motor takes a direction rather than a position, and stopping is the zero
 * value — the same zero that switches a light off.
 */
export const travelValue = (travel: TravelKind): number => {
  switch (travel) {
    case TRAVEL.OPEN:
      return DIN_VALUE.INCREASE;
    case TRAVEL.CLOSE:
      return DIN_VALUE.DECREASE;
    case TRAVEL.STOP:
      return DIN_VALUE.MIN;
    default:
      return assertNever(travel);
  }
};

/**
 * The console labels DIN channels 1-4 the way the module is labelled, and so
 * does the firmware: `dinoperate` and `getdinstatus` count channels from one,
 * and a reply's `channel` is that same one-based number. The reference's 0-15
 * is the range the parser accepts, not the base of the numbering.
 */
export const DIN_CHANNEL_NUMBERING = {
  CONSOLE_FIRST: 1,
  FIRMWARE_FIRST: 1,
} as const;

/** The console labels a DIN channel from 1; the firmware counts from 1 too. */
export const firmwareChannel = (consoleChannel: number): number =>
  consoleChannel - DIN_CHANNEL_NUMBERING.CONSOLE_FIRST + DIN_CHANNEL_NUMBERING.FIRMWARE_FIRST;

/** The number the technician reads for a channel the firmware reported. */
export const consoleChannel = (firmwareChannelNumber: number): number =>
  firmwareChannelNumber - DIN_CHANNEL_NUMBERING.FIRMWARE_FIRST + DIN_CHANNEL_NUMBERING.CONSOLE_FIRST;

/** Channel numbers a rail of this size shows: 1 to `channels`. */
export const consoleChannels = (channels: number): readonly number[] =>
  Array.from({ length: channels }, (_unused, index) =>
    consoleChannel(DIN_CHANNEL_NUMBERING.FIRMWARE_FIRST + index),
  );
